import { Lexer, Tokenizer, type Token, type Tokens } from 'marked';

import { MESSAGE_URL_PATTERN, toOpenableUrl } from '@/util/messageLinks';

/**
 * Turns the text of a reply, the question or a reference list into a small tree the
 * renderer draws with the app's own Text components.
 *
 * Specialists and submitters type plain text with the odd bit of Markdown, so the parse is
 * deliberately narrower than Markdown: lists, bold and italic, headings and line breaks are
 * read, and every rule that could swallow clinical shorthand is turned off. A line that starts
 * with "> 65 years" stays a line, "~5 cm" is not struck through, and a bare "--" under a label is
 * not a heading. The tokenizer is marked's, which handles the nesting and flanking rules that a
 * hand-written parser keeps getting wrong, and nothing here ever produces HTML.
 */
export type InlineNode =
  | { kind: 'text'; text: string }
  | { kind: 'strong'; children: InlineNode[] }
  | { kind: 'emphasis'; children: InlineNode[] }
  | { kind: 'code'; text: string }
  | { kind: 'link'; href: string; children: InlineNode[]; label?: string }
  | { kind: 'break' };

export type ListItemNode = { children: InlineNode[]; blocks: Block[] };

export type Block =
  | { kind: 'paragraph'; children: InlineNode[]; label: boolean }
  | { kind: 'heading'; children: InlineNode[] }
  | { kind: 'list'; ordered: boolean; start: number; items: ListItemNode[] };

const BULLET_MARKER = '[-*•●▪◦–]';

const LIST_ITEM = new RegExp(`^(\\s*)(?:(${BULLET_MARKER})\\s+|([•●▪◦])\\s*)(.*)$`);

const NUMBERED_ITEM = /^(\s*)(\d{1,9})([.)])\s+(.*)$/;

const INLINE_TAGS = 'i|em|b|strong|u|scp|sub|sup';

const SUPERSCRIPT: Record<string, string> = {
  '0': '⁰',
  '1': '¹',
  '2': '²',
  '3': '³',
  '4': '⁴',
  '5': '⁵',
  '6': '⁶',
  '7': '⁷',
  '8': '⁸',
  '9': '⁹',
  '+': '⁺',
  '-': '⁻',
};

const SUBSCRIPT: Record<string, string> = {
  '0': '₀',
  '1': '₁',
  '2': '₂',
  '3': '₃',
  '4': '₄',
  '5': '₅',
  '6': '₆',
  '7': '₇',
  '8': '₈',
  '9': '₉',
};

function mapDigits(text: string, table: Record<string, string>): string | null {
  const mapped = [...text].map((char) => table[char]);

  return mapped.every(Boolean) ? mapped.join('') : null;
}

/**
 * Journal citations arrive with the publisher's inline tags, `<i>EGFR</i>` or `<scp>CNS</scp>`,
 * and with the title hard wrapped around them. This is not HTML, so the tags would show as
 * typed. Only the tags journals use are read: the line break the wrap left around a tag is
 * closed up, a superscript or subscript made of digits becomes the Unicode character, and the
 * rest of the tag is dropped and its words kept. `<5%` and `>10%` are not tags and are left.
 */
function stripInlineTags(text: string): string {
  const tag = new RegExp(`</?(?:${INLINE_TAGS})\\s*>`, 'i');

  if (!tag.test(text)) {
    return text;
  }

  return text
    .replace(new RegExp(`[ \\t]*\\n[ \\t]+(?=</?(?:${INLINE_TAGS})\\s*>)`, 'gi'), ' ')
    .replace(
      new RegExp(
        `(</(?:${INLINE_TAGS})\\s*>)[ \\t]*\\n[ \\t]+(?!(?:[-*•●▪◦–]|\\d{1,9}[.)])\\s)(?=(?:([-–])(?=\\S)|\\S))`,
        'gi'
      ),
      (_match, close: string, dash: string) => (dash ? close : `${close} `)
    )
    .replace(
      /<sup>([0-9+-]+)<\/sup>/gi,
      (match, digits: string) => mapDigits(digits, SUPERSCRIPT) ?? match
    )
    .replace(
      /<sub>([0-9]+)<\/sub>/gi,
      (match, digits: string) => mapDigits(digits, SUBSCRIPT) ?? match
    )
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(new RegExp(`</?(?:${INLINE_TAGS})\\s*>`, 'gi'), '');
}

/**
 * A star between two letters or digits is part of a name, `UGT1A1*28`, `HLA-A*02:01` or
 * `G646fs*12`, and the same goes for `/*28` in a genotype. Left alone, Markdown pairs two of
 * them into italics and the stars disappear from the clinical text, so each is escaped.
 */
function escapeNameStars(text: string): string {
  const escape = (part: string) =>
    part
      .replace(/(?<=[A-Za-z0-9)\]])\*(?=[A-Za-z0-9])/g, '\\*')
      .replace(/(?<=\/)\*(?=[0-9])/g, '\\*');

  let out = '';

  let cursor = 0;

  for (const match of text.matchAll(new RegExp(MESSAGE_URL_PATTERN.source, 'gi'))) {
    const start = match.index ?? 0;

    out += escape(text.slice(cursor, start)) + match[0];

    cursor = start + match[0].length;
  }

  return out + escape(text.slice(cursor));
}

function cleanInlineText(text: string): string {
  return escapeNameStars(stripInlineTags(text));
}

/**
 * Makes plain text that is not quite Markdown read the way its author meant.
 *
 * Bullets typed as a dash, an en dash or a bullet character become `-` items. A list that follows
 * a line of prose gets a blank line before it, so a label such as "Options:" stays its own
 * line instead of being swallowed into the paragraph above, and a numbered list that does not
 * start at 1 still starts a list. A list gets a blank line after it when prose follows at the
 * margin, so that prose is not read as a continuation of the last item. Indentation is rewritten
 * to four spaces per level, which nests under both bullets and numbers.
 */
export function normalizePlainText(text: string): string {
  const lines = cleanInlineText(text.replace(/\r\n?/g, '\n')).replace(/\t/g, '    ').split('\n');

  const out: string[] = [];

  /** Indent widths of the open nesting levels, outermost first. */
  let indents: number[] = [];

  let inList = false;

  for (const line of lines) {
    const numbered = NUMBERED_ITEM.exec(line);

    const bullet = numbered ? null : LIST_ITEM.exec(line);

    if (!numbered && !bullet) {
      const blank = line.trim() === '';

      const continuation = inList && /^\s+\S/.test(line);

      if (inList && !blank && !continuation) {
        out.push('');
      }

      if (blank || !continuation) {
        inList = false;

        indents = [];
      }

      out.push(blank ? '' : line);

      continue;
    }

    const indent = (numbered ?? bullet)![1].length;

    if (!inList && out.length > 0 && out[out.length - 1].trim() !== '') {
      out.push('');
    }

    while (indents.length > 0 && indent < indents[indents.length - 1]) {
      indents.pop();
    }

    if (indents.length === 0 || indent > indents[indents.length - 1]) {
      indents.push(indent);
    }

    inList = true;

    const prefix = '    '.repeat(indents.length - 1);

    if (numbered) {
      out.push(`${prefix}${numbered[2]}${numbered[3]} ${numbered[4]}`);
    } else {
      out.push(`${prefix}- ${bullet![4]}`);
    }
  }

  return out.join('\n');
}

const ENTITIES: Record<string, string> = {
  '&amp;': '&',
  '&lt;': '<',
  '&gt;': '>',
  '&quot;': '"',
  '&#39;': "'",
};

/**
 * marked escapes text for HTML as it tokenizes. This is not HTML, so the five escapes it makes,
 * and numeric references, go back to the characters the author typed.
 */
function decodeEntities(text: string): string {
  return text
    .replace(/&(?:amp|lt|gt|quot|#39);/g, (entity) => ENTITIES[entity])
    .replace(/&#(\d{1,7});/g, (_match, code: string) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]{1,6});/gi, (_match, code: string) =>
      String.fromCodePoint(parseInt(code, 16))
    );
}

const PMID_PATTERN = /\bPMID:?\s*(\d{1,9})\b/i;

const DOI_PATTERN = /\b10\.\d{4,9}\/[^\s<>"]+/;

const LINKABLE = new RegExp(
  `(?<url>${MESSAGE_URL_PATTERN.source})|(?<pmid>${PMID_PATTERN.source})|(?<doi>${DOI_PATTERN.source})`,
  'gi'
);

/** A DOI ends where the sentence does, so closing punctuation is not part of it. */
export function trimDoi(doi: string): string {
  let end = doi.length;

  while (end > 0) {
    const last = doi[end - 1];

    const unmatchedParen =
      last === ')' && doi.slice(0, end).split(')').length > doi.slice(0, end).split('(').length;

    if (/[.,;:!?'\]}]/.test(last) || unmatchedParen) {
      end -= 1;
    } else {
      break;
    }
  }

  return doi.slice(0, end);
}

/**
 * Splits text into plain runs and links for a URL, a `PMID: 12345678` citation, or a bare DOI.
 * The PubMed and doi.org links are built here, so a citation a specialist typed as a few
 * characters is one tap from the paper.
 */
export function linkifyText(text: string): InlineNode[] {
  const nodes: InlineNode[] = [];

  let cursor = 0;

  for (const match of text.matchAll(LINKABLE)) {
    const start = match.index ?? 0;

    let matched = match[0];

    let href: string;

    if (match.groups?.url !== undefined) {
      href = toOpenableUrl(matched);
    } else if (match.groups?.pmid !== undefined) {
      const pmid = /(\d+)$/.exec(matched)?.[1] ?? '';

      href = `https://pubmed.ncbi.nlm.nih.gov/${pmid}/`;
    } else {
      matched = trimDoi(matched);

      href = `https://doi.org/${matched}`;
    }

    if (start > cursor) {
      nodes.push({ kind: 'text', text: text.slice(cursor, start) });
    }

    nodes.push({ kind: 'link', href, children: [{ kind: 'text', text: matched }] });

    cursor = start + matched.length;
  }

  if (cursor < text.length) {
    nodes.push({ kind: 'text', text: text.slice(cursor) });
  }

  return nodes;
}

function inlineNodes(tokens: Token[] | undefined, insideLink = false): InlineNode[] {
  const nodes: InlineNode[] = [];

  for (const token of tokens ?? []) {
    switch (token.type) {
      case 'text':
      case 'escape': {
        const inner = (token as Tokens.Text).tokens;

        if (inner && inner.length > 0) {
          nodes.push(...inlineNodes(inner, insideLink));
        } else {
          const text = decodeEntities(token.text);

          nodes.push(...(insideLink ? [{ kind: 'text' as const, text }] : linkifyText(text)));
        }

        break;
      }

      case 'strong':
        nodes.push({
          kind: 'strong',
          children: inlineNodes((token as Tokens.Strong).tokens, insideLink),
        });

        break;

      case 'em':
        nodes.push({
          kind: 'emphasis',
          children: inlineNodes((token as Tokens.Em).tokens, insideLink),
        });

        break;

      case 'codespan':
        nodes.push({ kind: 'code', text: decodeEntities((token as Tokens.Codespan).text) });

        break;

      case 'br':
        nodes.push({ kind: 'break' });

        break;

      case 'link':
        nodes.push({
          kind: 'link',
          href: toOpenableUrl((token as Tokens.Link).href),
          children: inlineNodes((token as Tokens.Link).tokens, true),
        });

        break;

      case 'image':
        nodes.push({ kind: 'text', text: decodeEntities((token as Tokens.Image).text) });

        break;

      default:
        nodes.push({ kind: 'text', text: token.raw });
    }
  }

  return nodes;
}

/**
 * The tokenizer rules switched off, and why each would misread clinical text. What is left is
 * headings, lists, paragraphs and inline emphasis.
 */
const DISABLED_RULES = [
  'code',
  'fences',
  'hr',
  'blockquote',
  'html',
  'def',
  'table',
  'lheading',
  'url',
  'del',
] as const;

function createLexer(): Lexer {
  const tokenizer = new Tokenizer();

  const rules = tokenizer as unknown as Record<string, () => undefined>;

  for (const rule of DISABLED_RULES) {
    rules[rule] = () => undefined;
  }

  return new Lexer({ gfm: true, breaks: true, tokenizer });
}

/** An indented line the code rule would have claimed arrives with its indent; it is prose. */
function trimStart(nodes: InlineNode[]): InlineNode[] {
  const [first, ...rest] = nodes;

  return first?.kind === 'text' ? [{ kind: 'text', text: first.text.trimStart() }, ...rest] : nodes;
}

function blockNodes(tokens: Token[]): Block[] {
  const blocks: Block[] = [];

  for (const token of tokens) {
    if (token.type === 'paragraph' || token.type === 'text') {
      blocks.push({
        kind: 'paragraph',
        children: trimStart(inlineNodes((token as Tokens.Paragraph).tokens)),
        label: false,
      });
    } else if (token.type === 'heading') {
      blocks.push({ kind: 'heading', children: inlineNodes((token as Tokens.Heading).tokens) });
    } else if (token.type === 'list') {
      const list = token as Tokens.List;

      blocks.push({
        kind: 'list',
        ordered: list.ordered,
        start: typeof list.start === 'number' ? list.start : 1,
        items: list.items.map((item) => {
          const nested = blockNodes(item.tokens);

          const first = nested[0];

          const own = first && first.kind === 'paragraph' ? first : null;

          return {
            children: own ? own.children : [],
            blocks: own ? nested.slice(1) : nested,
          };
        }),
      });
    } else if (token.type !== 'space') {
      blocks.push({
        kind: 'paragraph',
        children: [{ kind: 'text', text: token.raw.trim() }],
        label: false,
      });
    }
  }

  blocks.forEach((block, index) => {
    const next = blocks[index + 1];

    if (block.kind === 'paragraph' && next?.kind === 'list') {
      const last = block.children[block.children.length - 1];

      block.label = last?.kind === 'text' && last.text.trimEnd().endsWith(':');
    }
  });

  return blocks;
}

/** Parses a turn, a question or a reference list into blocks ready to draw. */
export function parseAnswersMarkdown(text: string): Block[] {
  return blockNodes(createLexer().lex(normalizePlainText(text)));
}

/**
 * Groups blocks into the units a fold may cut between. A label that introduces a list travels
 * with that list, so a fold never leaves "Options include:" dangling above nothing.
 */
export function groupUnits(blocks: Block[]): Block[][] {
  const units: Block[][] = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];

    if (block.kind === 'paragraph' && block.label && index + 1 < blocks.length) {
      units.push([block, blocks[index + 1]]);

      index += 1;
    } else {
      units.push([block]);
    }
  }

  return units;
}
